import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "(ab)Using d3.js to make a Pong game",
  "description": "",
  "published": "2014-05-28T00:00:00.000Z",
  "redirect_from": ["/blog/abusing-d3-js-to-make-a-pong-game/swizec/6578"],
  "categories": "Cascading Style Sheets, CSS, d3.js, Graphics, HTML, Scalable Vector Graphics, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`D3.js is a data visualization library first and foremost. That's what people use it for. To make shiny things that blow everyone's minds.`}</p>
    <p>{`But D3 is more than that. It's a powerful `}<a parentName="p" {...{
        "href": "http://www.w3.org/Graphics/SVG/",
        "title": "Scalable Vector Graphics"
      }}>{`SVG`}</a>{` manipulation library. Yes, some people would say `}<em parentName="p">{`"But you don't need an SVG manipulation library! You can just write SVG like you do `}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/HTML",
          "title": "HTML"
        }}>{`HTML`}</a>{`"`}</em>{`. Those people are silly and probably write their own time manipulation functions as well.`}</p>
    <p>{`Recently I made a simple game of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Pong",
        "title": "Pong"
      }}>{`Pong`}</a>{` using D3. Nothing fancy, just two paddles that you can drag around, a ball that bounces to and fro, two score counters, and reacting to orientation changes on mobile devices. There isn't even a start or stop button.`}</p>
    <p>{`You can play the game `}<a parentName="p" {...{
        "href": "http://swizec.github.io/d3-pong/"
      }}>{`here`}</a>{`, and see the code `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/d3-pong"
      }}>{`here`}</a>{`.`}</p>
    <p>{`It was a quick project though, so it doesn't work on Firefox because of a weird SVG canvas sizing bug (I needed it to spread the whole screen) and some people have told me dragging the paddles doesn't work on desktop. Worked for me. `}<em parentName="p">{`shrug`}</em></p>
    <h2 {...{
      "id": "putting-it-together"
    }}>{`Putting it together`}</h2>
    <p>{`There really isn't much to making a game like this with D3.`}</p>
    <p>{`First we need some minimal HTML:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<meta charset="utf-8" />
<meta
  name="viewport"
  content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
/>
<title>D3 pong</title>
<link
  href="http://fonts.googleapis.com/css?family=Overlock"
  rel="stylesheet"
  type="text/css"
/>
<link rel="stylesheet" href="style.css" />

<main></main>

<script src="http://d3js.org/d3.v3.min.js" charset="utf-8"></script>
<script src="d3-pong.js"></script>
`}</code></pre>
    <p>{`The HTML isn't interesting. Our game will go in the `}<inlineCode parentName="p">{`<main></main>`}</inlineCode>{` tag. The rest is about loading necessary files and telling mobile browsers not to act funny.`}</p>
    <p>{`We also need some `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Cascading_Style_Sheets",
        "title": "Cascading Style Sheets"
      }}>{`CSS`}</a>{` to make sure our game fills the entire screen and things look decent.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`html,
body,
main {
  height: 100%;
  padding: 0;
  margin: 0;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

svg {
  width: 100%;
  height: 99%; /* gets rid of scrollbar */
}

text {
  font-family: "Overlock", cursive;
  font-size: 1.5em;
}

line {
  stroke: black;
  stroke-width: 2;
}

.area {
  fill: white;
  stroke: "red";
}
`}</code></pre>
    <h2 {...{
      "id": "the-base-elements"
    }}>{`The base elements`}</h2>
    <p>{`After that ground work, the fun begins.`}</p>
    <p>{`We create the SVG element, define some useful margins, and a helper function that turns css properties like `}<inlineCode parentName="p">{`"10px"`}</inlineCode>{` into numbers.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var svg = d3.select("main").append("svg"),
  margin = { top: 10, right: 10, bottom: 10, left: 10 },
  parse = function (N) {
    return Number(N.replace("px", ""));
  };
`}</code></pre>
    <p>{`Simple.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`Screen`}</inlineCode>{` function will always tell us how much room we've got.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var Screen = function () {
  return {
    width: parse(svg.style("width")),
    height: parse(svg.style("height")),
  };
};
`}</code></pre>
    <p>{`Next up, making a paddle. For extra fun, I made it so creating a paddle returns a function we can call whenever we want to update the paddle's position.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Paddle = function (which) {
            var width = 5,
                area = svg.append('rect')
                    .classed('area', true)
                    .attr({width: width*7}),
                paddle = svg.append('rect')
                    .classed('paddle', true)
                    .classed(which+"_paddle", true)
                    .attr({width: 5}),
                update = function (x, y) {
                    var height = Screen().height*0.15;

                    paddle.attr({
                        x: x,
                        y: y,
                        height: height
                    });
                    area.attr({
                        x: x-width*5/2,
                        y: y,
                        height: height
                    });
                    return update;
                };
`}</code></pre>
    <p>{`Because fingers are fat and paddles are thin, we defined an `}<inlineCode parentName="p">{`area`}</inlineCode>{` that's bigger than the actual paddle. This will be used as the drag handle. Then we've got the `}<inlineCode parentName="p">{`paddle`}</inlineCode>{` itself - both of these are just SVG rectangles.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`update`}</inlineCode>{` function si a little bit more interesting, but not much. It just takes an `}<inlineCode parentName="p">{`x, y`}</inlineCode>{` position and updates `}<inlineCode parentName="p">{`paddle`}</inlineCode>{` and `}<inlineCode parentName="p">{`area`}</inlineCode>{`.`}</p>
    <p>{`To make paddles draggable either with a mouse or a finger, we're going to use D3's draggable behavior. A pre-built function we can call on any SVG element to create listeners for all relevant events.`}</p>
    <p>{`All it requires of us is defining what actually happens when something gets dragged.`}</p>
    <p>{`This goes inside the `}<inlineCode parentName="p">{`Paddle`}</inlineCode>{` function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// make paddle draggable
            var drag = d3.behavior.drag()
                    .on("drag", function () {
                        var y = parse(area.attr("y")),
                            height = Screen().height*0.1;

                        update(parse(paddle.attr("x")),
                               Math.max(margin.top,
                                        Math.min(parse(paddle.attr("y"))+d3.event.dy,
                                                 Screen().height-margin.bottom-height)));

                    })
                    .origin(function () {
                        return {x: parse(area.attr("x")),
                                y: parse(area.attr("y"))};
                    });

            area.call(drag);

            return update;
        },
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`"drag"`}</inlineCode>{` event represents any type of either mouse or touch event that might represent dragging. In the callback we essentially just call the `}<inlineCode parentName="p">{`update`}</inlineCode>{` function with new coordinates. All that `}<inlineCode parentName="p">{`Math.max`}</inlineCode>{` and `}<inlineCode parentName="p">{`Math.min`}</inlineCode>{` nonsense makes sure paddles can't be dragged out of the screen.`}</p>
    <p><inlineCode parentName="p">{`.origin`}</inlineCode>{` is something this behaviour needs to calculate positions properly. It's best to just set it to whatever is the current position of our element.`}</p>
    <p><inlineCode parentName="p">{`area.call(drag);`}</inlineCode>{` activates the draggable behaviour on our draggable `}<inlineCode parentName="p">{`area`}</inlineCode>{`.`}</p>
    <p>{`Next up - a function that keeps score.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// generates a score, returns function for updating value and repositioning score
        Score = function (x) {
            var value = 0,
                score = svg.append('text')
                    .text(value);

            return function f(inc) {
                value += inc;

                score.text(value)
                    .attr({x: Screen().width*x,
                           y: margin.top*3});
                return f;
            };
        },
`}</code></pre>
    <p>{`We're going for the trick with returning update functions again. But other than that it's really simple, just add a `}<inlineCode parentName="p">{`text`}</inlineCode>{` element to the drawing area and give it a value. Don't worry about that repositioning stuff for now.`}</p>
    <p>{`Nearly the same goes for the middle line - add a line, make sure it can be moved when needed.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// generates middle line, returns function for updating position
        Middle = function () {
            var line = svg.append('line');

            return function f() {
                var screen = Screen();

                line.attr({
                    x1: screen.width/2,
                    y1: margin.top,
                    x2: screen.width/2,
                    y2: screen.height-margin.bottom
                });
                return f;
            };
`}</code></pre>
    <p>{`The ball is going to be a bit more fun. Not only does it have to draw a simple circle and be able to move it around, it should also react to hitting obstacles and updating scores.`}</p>
    <p>{`This time we're going to return a function that does a full step of the main animation. Things might get hairy.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Ball = function () {
            var R = 5,
                ball = svg.append('circle')
                    .classed("ball", true)
                    .attr({r: R,
                           cx: Screen().width/2,
                           cy: Screen().height/2}),
                scale = d3.scale.linear().domain([0, 1]).range([-1, 1]),
                vector = {x: scale(Math.random()),
                          y: scale(Math.random())},
                speed = 7;
`}</code></pre>
    <p>{`We started with the simple stuff - drawing a ball, defining a random vector, and making up a speed that looked good on my screen.`}</p>
    <p>{`The collision logic is hairier.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var hit_paddle = function (y, paddle) {
    return (
      y - R > parse(paddle.attr("y")) &&
      y + R < parse(paddle.attr("y")) + parse(paddle.attr("height"))
    );
  },
  collisions = function () {
    var x = parse(ball.attr("cx")),
      y = parse(ball.attr("cy")),
      left_p = d3.select(".left_paddle"),
      right_p = d3.select(".right_paddle");

    // collision with top or bottom
    if (y - R < margin.top || y + R > Screen().height - margin.bottom) {
      vector.y = -vector.y;
    }

    // bounce off right paddle or score
    if (x + R > parse(right_p.attr("x"))) {
      if (hit_paddle(y, right_p)) {
        vector.x = -vector.x;
      } else {
        return "left";
      }
    }

    // bounce off left paddle or score
    if (x - R < parse(left_p.attr("x")) + parse(left_p.attr("width"))) {
      if (hit_paddle(y, left_p)) {
        vector.x = -vector.x;
      } else {
        return "right";
      }
    }

    return false;
  };
`}</code></pre>
    <p>{`Hokay.`}</p>
    <p><inlineCode parentName="p">{`hit_paddle`}</inlineCode>{` is a helper function that tells us whether the ball is touching a paddle - paddle position minus ball radius. Simple.`}</p>
    <p><inlineCode parentName="p">{`collisions`}</inlineCode>{` looks hairy, but it's very repetitive:`}</p>
    <ul>
      <li parentName="ul">{`if the ball hits top or bottom edge, its vertical position should flip.`}</li>
      <li parentName="ul">{`if the ball is to the right enough to hit the paddle, it will either flip its horizontal direction, or tell the calling code that `}<inlineCode parentName="li">{`"right"`}</inlineCode>{` messed up`}</li>
      <li parentName="ul">{`same thing on the left`}</li>
      <li parentName="ul">{`if nothing happens, return false`}</li>
    </ul>
    <p>{`The last part of the `}<inlineCode parentName="p">{`Paddle`}</inlineCode>{` function is the function that performs an animation step.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`return function f(left, right, delta_t) {
                var screen = Screen(),
                    // this should pretend we have 100 fps
                    fps = delta_t > 0 ? (delta_t/1000)/100 : 1;

                ball.attr({
                    cx: parse(ball.attr("cx"))+vector.x*speed*fps,
                    cy: parse(ball.attr("cy"))+vector.y*speed*fps
                });

                var scored = collisions();

                if (scored) {
                    if (scored == "left") {
                        left.score(1);
                    }else{
                        right.score(1);
                    }
                    return true;
                }

                return false;
            };
        };
`}</code></pre>
    <p>{`It's pretty simple. First we update the ball's position according to the current vector, then we check for collisions and update scores if need be. Lastly we return `}<inlineCode parentName="p">{`true`}</inlineCode>{` or `}<inlineCode parentName="p">{`false`}</inlineCode>{` depending on whether we want the current animation to continue or not.`}</p>
    <h2 {...{
      "id": "the-main-bit"
    }}>{`The main bit`}</h2>
    <p>{`Now that we've got all the elements, which sneakily contain most of our code already, it's time to put it all in motion.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// generate starting scene
var left = {
    score: Score(0.25)(0),
    paddle: Paddle("left")(margin.left, Screen().height / 2),
  },
  right = {
    score: Score(0.75)(0),
    paddle: Paddle("right")(Screen().width - margin.right, Screen().height / 2),
  },
  middle = Middle()(),
  ball = Ball();
`}</code></pre>
    <p><inlineCode parentName="p">{`left`}</inlineCode>{` and `}<inlineCode parentName="p">{`right`}</inlineCode>{` hold each player's score and paddle update functions, and `}<inlineCode parentName="p">{`middle`}</inlineCode>{` and `}<inlineCode parentName="p">{`ball`}</inlineCode>{` are the middle line and the ball.`}</p>
    <p>{`We also have to react to window resizing. This sneakily captures orientation changes as well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// detect window resize events (also captures orientation changes)
d3.select(window).on("resize", function () {
  var screen = Screen();

  left.score(0);
  left.paddle(margin.left, screen.height / 2);
  right.score(0);
  right.paddle(screen.width - margin.right, screen.height / 2);

  middle();
});
`}</code></pre>
    <p>{`When the screen changes, we just update the position of everything. See how always returning an update function made our life easier?`}</p>
    <p>{`And finally, we start the animation.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// start animation timer that runs until a player scores
// then reset ball and start again
function run() {
  var last_time = Date.now();
  d3.timer(function () {
    var now = Date.now(),
      scored = ball(left, right, now - last_time),
      last_time = now;

    if (scored) {
      d3.select(".ball").remove();
      ball = Ball();
      run();
    }
    return scored;
  }, 500);
}
run();
`}</code></pre>
    <p>{`We used a `}<inlineCode parentName="p">{`d3.timer`}</inlineCode>{` to create a custom animation loop that's tied to the graphics speed of the user's device. To counter for this, we feed a time delta into our `}<inlineCode parentName="p">{`ball`}</inlineCode>{` animation function to create the appearance of consistent speed.`}</p>
    <p>{`Actual game developers told me I have to do that, so I did.`}</p>
    <p>{`When a user scores, we reset the ball to its current position and re-run everything. `}<inlineCode parentName="p">{`d3.timer`}</inlineCode>{`'s main loop runs for as long as the function keeps returning `}<inlineCode parentName="p">{`false`}</inlineCode>{`. We took care of that by returning `}<inlineCode parentName="p">{`scored`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`And that's it. (ab)Using D3 to make a simple Pong game because we can. It was a fun hack, there are a million better tools you could use to make this, but I had fun.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=7a0131b4-3a05-4d09-9408-40fa8cb003ce",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      